import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Input, Image, Popconfirm, Button, message, Table, Modal, Select, Form, Switch } from "antd";
import { EyeOutlined, EditOutlined, DeleteOutlined, UserOutlined, UndoOutlined } from "@ant-design/icons";

import { getUsers, deleteUser, inviteUsers, archiveUser, unArchiveUser } from "../../api/user";
import { getFileUrl } from "../../api/file";

import PageHeader from "../../components/shared/PageHeader";
import BlueButton from "../../components/shared/BlueButton";
import { PER_PAGE } from "../../configs";

const { Search } = Input;

export default function UsersView() {
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [users, setUsers] = useState(null);
    const [searchQuery, setSearchQuery] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const perPage = PER_PAGE;
    const [form] = Form.useForm();

    const handleCancel = () => {
        form.resetFields();
        setShowInviteModal(false);
    }

    const onSubmit = () => {
        form.submit();
    }

    const onFinish = async (values) => {
        const v = await form.validateFields();
        setIsLoading(true);
        try {
            const res = await inviteUsers(v);
            if (res.Code === 1) {
                message.success("Invitation sent successfully");
                form.resetFields();
                setShowInviteModal(false);
            } else {
                message.error(res.Data?.message || "Something went wrong, please try again later");
            }
        }
        catch (error) {
            message.error("Something went wrong, please try again later");
            setIsLoading(false)
        }
        setIsLoading(false);

    }

    const onFinishFailed = (errorInfo) => {
        console.log("Failed:", errorInfo);
    }

    const validateEmails = (_, value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value.every(email => emailRegex.test(email))) {
            return Promise.resolve();
        }
        return Promise.reject(new Error('Please enter valid emails'));
    }

    const columns = [
        {
            title: "SN",
            dataIndex: "id",
            key: "id",
            render: (text, record, index) => index + 1,
        },
        {
            title: "Image",
            dataIndex: "image",
            key: "image",
            render: (text, record) => {
                if (!record.avatar) return <div className="bg-orange-300 w-[48px] h-[48px] flex items-center justify-center rounded-full "><UserOutlined className="text-black" /></div>;
                return <Image src={getFileUrl(record.avatar)} preview={false} style={{ width: "48px", height: "48px", objectFit: "contain" }} className="rounded-full" />
            },
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (text, record) => <p>{record.firstName} {record.lastName}</p>
        },
        {
            title: "Phone",
            dataIndex: "phone",
            key: "phone",
        },
        {
            title: 'License',
            dataIndex: 'license',
            key: 'license',
            render: (text) => <p className="capitalize">{text}</p>
        },
        {
            title: 'Ticket',
            dataIndex: 'ticket',
            key: 'ticket',
            render: (text) => <p className="capitalize">{text}</p>
        },
        {
            title: "Actions",
            key: "action",
            render: (text, record) => {
                const isArchived = record.isArchived;

                return (
                    <div className="flex gap-2">
                        {/* View User */}
                        <Link to={`/users/${record.id}`}>
                            <Button type="text" icon={<EyeOutlined />} />
                        </Link>
                        {/* Edit User */}
                        <Link to={`/users/form/${record.id}`}>
                            <Button type="text" icon={<EditOutlined />} />
                        </Link>
                        {/* Archive or Unarchive User */}
                        {isArchived ? (
                            // Unarchive/Restore button for archived users
                            <Popconfirm
                                title="Unarchive the user"
                                description="Are you sure to unarchive this user?"
                                onConfirm={async () => {
                                    await unArchiveUser(record.id);
                                    message.success("User unarchived successfully");
                                    await fetchUsers({ page, perPage, q: searchQuery, showArchived });
                                }}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button size="small" icon={<UndoOutlined />} style={{ color: '#52c41a', borderColor: '#52c41a' }}>
                                    Unarchive
                                </Button>
                            </Popconfirm>
                        ) : (
                            // Archive button for active users
                            <Popconfirm
                                title="Archive the user"
                                description="Are you sure to archive this user?"
                                onConfirm={async () => {
                                    await archiveUser(record.id);
                                    message.success("User archived successfully");
                                    await fetchUsers({ page, perPage, q: searchQuery, showArchived });
                                }}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button size="small" icon={<DeleteOutlined />} danger>
                                    Archive
                                </Button>
                            </Popconfirm>
                        )}
                    </div>
                )
            },
        }
    ]

    const fetchUsers = useCallback(async ({ page, q, perPage, showArchived }) => {
        try {
            setIsLoading(true);
            const { total, results } = await getUsers({
                page, perPage, q, showArchived
            });
            setTotal(total);
            setUsers(results);
            setIsLoading(false);
        } catch (error) {

        }
    }, [])

    useEffect(() => {
        fetchUsers({ page, perPage, q: searchQuery, showArchived });
    }, [fetchUsers, page, perPage, searchQuery, showArchived])

    return (
        <main className="min-h-full flex flex-col space-y-6">
            <PageHeader title="Users" home="App" route="/" />

            <div className="flex-grow bg-white ml-6 p-6 flex flex-col space-y-6">
                <div className="flex justify-between">
                    <div className="flex gap-2">
                        <Link to="/users/form">
                            <BlueButton type="primary" >Add New User</BlueButton>
                        </Link>

                        <Button
                            onClick={() => {
                                setShowInviteModal(true);
                            }}
                        >Invite Users</Button>
                    </div>
                    <div className="flex gap-4 items-center">
                        <div className="flex gap-2 items-center">
                            <span>Show Archived:</span>
                            <Switch
                                checked={showArchived}
                                onChange={(checked) => {
                                    setShowArchived(checked);
                                }}
                            />
                        </div>
                        <Search
                            placeholder="Search Users"
                            style={{
                                width: 200,
                            }}
                            allowClear
                            onSearch={(q) => setSearchQuery(q)}
                        />
                    </div>
                </div>


                <section>
                    <Table
                        dataSource={users}
                        columns={columns}
                        loading={isLoading}
                        pagination={{ total, pageSize: perPage, current: page, onChange: (page) => setPage(page) }}
                    />

                </section>

                <Modal title="Invite Users" open={showInviteModal} onCancel={handleCancel}
                    onOk={onSubmit}>
                    <div className="flex flex-col gap-4">
                        <p>Let's begin your TrakHire journey by inviting your team members.</p>

                        <Form form={form} layout="vertical" onFinish={onFinish} onFinishFailed={onFinishFailed}>
                            <Form.Item
                                name={"emails"}
                                rules={[
                                    { required: true, message: "Please enter at least one email" },
                                    { validator: validateEmails }
                                ]}
                            >
                                <Select mode="tags" open={false} placeholder="Invite Users" className="w-full" suffixIcon={<div />} />
                            </Form.Item>
                        </Form>
                    </div>
                </Modal>

            </div>

        </main >
    )
}
